import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { FaClone, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useFunctionalUnitCascade } from '../shared/useFunctionalUnitCascade';
import FunctionalUnitTag from '../shared/FunctionalUnitTag';

const TRASH_ID = '__trash__';
const UNCATEGORIZED_LABEL = 'Non catégorisé';

// Collision: pointer-within for trash (exact hover), closestCenter for columns
function collisionDetection(args) {
  const pointerCollisions = pointerWithin(args);
  const trashHit = pointerCollisions.find((c) => c.id === TRASH_ID);
  if (trashHit) return [trashHit];
  return closestCenter(args);
}

function RoomCard({ room, equipmentCount, onDuplicate, functionalUnits, onChangeFunctionalUnit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: room._id,
  });
  const style = { transform: CSS.Translate.toString(transform) };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={['reorg-room-card', isDragging ? 'reorg-room-card--dragging' : ''].join(' ')}
    >
      <div className="reorg-room-main">
        <div className="reorg-room-drag-area" {...listeners} {...attributes}>
          <span className="reorg-room-name">{room.name_room}</span>
          {equipmentCount > 0 && (
            <span className="reorg-room-equip">{equipmentCount} équip.</span>
          )}
        </div>
        <button
          className="reorg-room-dup-btn"
          onClick={(e) => { e.stopPropagation(); onDuplicate(room); }}
          title="Dupliquer"
        >
          <FaClone size={11} />
        </button>
      </div>
      <FunctionalUnitTag
        value={room.functional_unit}
        functionalUnits={functionalUnits}
        onChange={(newValue) => onChangeFunctionalUnit(room, newValue)}
        variant="room"
      />
    </div>
  );
}

function SectionColumn({ section, rooms, equipments, onDuplicate, onDuplicateSection, functionalUnits, onChangeSectionFunctionalUnit, onChangeRoomFunctionalUnit }) {
  const { setNodeRef, isOver } = useDroppable({ id: section._id });

  return (
    <div
      ref={setNodeRef}
      className={['reorg-column', isOver ? 'reorg-column--over' : ''].join(' ')}
    >
      <div className="reorg-column-header">
        <div className="reorg-column-header-row">
          <span className="reorg-column-title">{section.name_section}</span>
          <span className="reorg-column-count">
            {rooms.length} pièce{rooms.length !== 1 ? 's' : ''}
          </span>
          <button
            className="reorg-room-dup-btn reorg-section-dup-btn"
            onClick={() => onDuplicateSection(section)}
            title="Dupliquer la section"
          >
            <FaClone size={11} />
          </button>
        </div>
        <FunctionalUnitTag
          value={section.functional_unit}
          functionalUnits={functionalUnits}
          onChange={(newValue) => onChangeSectionFunctionalUnit(section, newValue)}
          variant="section"
        />
      </div>
      <div className="reorg-column-body">
        {rooms.map((room) => (
          <RoomCard
            key={room._id}
            room={room}
            equipmentCount={equipments.filter((e) => e.location.room_id === room._id).length}
            onDuplicate={onDuplicate}
            functionalUnits={functionalUnits}
            onChangeFunctionalUnit={onChangeRoomFunctionalUnit}
          />
        ))}
        {rooms.length === 0 && (
          <div className="reorg-column-empty">Déposer une pièce ici</div>
        )}
      </div>
    </div>
  );
}

function TrashDropZone({ visible }) {
  const { setNodeRef, isOver } = useDroppable({ id: TRASH_ID });

  return (
    <div
      ref={setNodeRef}
      className={[
        'reorg-trash-zone',
        visible ? 'reorg-trash-zone--visible' : '',
        isOver ? 'reorg-trash-zone--over' : '',
      ].join(' ')}
    >
      <FaTrash size={14} />
      <span>Glisser ici pour supprimer</span>
    </div>
  );
}

function OverlayCard({ room, equipments }) {
  const count = equipments.filter((e) => e.location.room_id === room._id).length;
  return (
    <div className="reorg-room-card reorg-room-card--overlay">
      <div className="reorg-room-drag-area">
        <span className="reorg-room-name">{room.name_room}</span>
        {count > 0 && <span className="reorg-room-equip">{count} équip.</span>}
      </div>
    </div>
  );
}

export default function ReorganizeView({
  sections, rooms, equipments, idBuilding, buildingData,
}) {
  const [localSections, setLocalSections] = useState(sections);
  const [localRooms, setLocalRooms] = useState(rooms);
  const [localEquipments, setLocalEquipments] = useState(equipments);
  const [activeRoom, setActiveRoom] = useState(null);

  const [showNewSectionForm, setShowNewSectionForm] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionFunctionalUnit, setNewSectionFunctionalUnit] = useState('');
  const [newSectionFrequentation, setNewSectionFrequentation] = useState('average');
  const [isCreatingSection, setIsCreatingSection] = useState(false);

  const [duplicatingRoom, setDuplicatingRoom] = useState(null);
  const [newRoomName, setNewRoomName] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);

  const [duplicatingSection, setDuplicatingSection] = useState(null);
  const [newSectionCopyName, setNewSectionCopyName] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = ({ active }) => {
    setActiveRoom(localRooms.find((r) => r._id === active.id) || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveRoom(null);
    if (!over) return;

    // ── Delete via trash zone ──
    if (over.id === TRASH_ID) {
      const roomToDelete = localRooms.find((r) => r._id === active.id);
      if (!roomToDelete) return;

      const result = await Swal.fire({
        title: 'Supprimer cette pièce ?',
        html: `<strong>${roomToDelete.name_room}</strong> et tous ses équipements et usages associés seront définitivement supprimés.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
        customClass: 'swal-custom',
      });

      if (!result.isConfirmed) return;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/delete_room/${active.id}`,
          { method: 'DELETE' }
        );
        if (response.ok) {
          setLocalRooms((prev) => prev.filter((r) => r._id !== active.id));
          toast.success('Pièce supprimée.');
        } else {
          toast.error('Erreur lors de la suppression.');
        }
      } catch {
        toast.error("Erreur de connexion à l'API.");
      }
      return;
    }

    // ── Move to another section ──
    const draggedRoom = localRooms.find((r) => r._id === active.id);
    if (!draggedRoom || draggedRoom.id_section === over.id) return;

    const prevSectionId = draggedRoom.id_section;
    const newSectionId = over.id;

    setLocalRooms((prev) =>
      prev.map((r) => (r._id === active.id ? { ...r, id_section: newSectionId } : r))
    );

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reorganize_sections`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ moves: [{ room_id: active.id, new_section_id: newSectionId }] }),
      });
      if (!response.ok) {
        setLocalRooms((prev) =>
          prev.map((r) => (r._id === active.id ? { ...r, id_section: prevSectionId } : r))
        );
        toast.error('Erreur lors du déplacement.');
      }
    } catch {
      setLocalRooms((prev) =>
        prev.map((r) => (r._id === active.id ? { ...r, id_section: prevSectionId } : r))
      );
      toast.error("Erreur de connexion à l'API.");
    }
  };

  const resetNewSectionForm = () => {
    setNewSectionName('');
    setNewSectionFunctionalUnit('');
    setNewSectionFrequentation('average');
    setShowNewSectionForm(false);
  };

  const handleCreateSection = async (e) => {
    e.preventDefault();
    if (!newSectionName.trim()) return;
    setIsCreatingSection(true);
    try {
      const sectionData = {
        _id: uuidv4(),
        id_building: idBuilding,
        name_section: newSectionName.trim(),
        person_count: 0,
        frequentation: newSectionFrequentation,
        functional_unit: newSectionFunctionalUnit,
      };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/load_new_section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionData),
      });
      if (response.ok) {
        setLocalSections((prev) => [...prev, sectionData]);
        resetNewSectionForm();
        toast.success('Section créée.');
      } else {
        toast.error('Erreur lors de la création de la section.');
      }
    } catch {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsCreatingSection(false);
    }
  };

  const openDuplicateForm = (room) => {
    setDuplicatingRoom(room);
    setNewRoomName(room.name_room + ' (copie)');
  };

  const handleDuplicateRoom = async (e) => {
    e.preventDefault();
    if (!newRoomName.trim() || !duplicatingRoom) return;
    setIsDuplicating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/duplicate_room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: duplicatingRoom._id,
          new_room_name: newRoomName.trim(),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setLocalRooms((prev) => [...prev, data.room]);
        setLocalEquipments((prev) => [...prev, ...data.equipments]);
        setDuplicatingRoom(null);
        setNewRoomName('');
        toast.success('Pièce dupliquée. Pensez à affecter les usages de la nouvelle pièce.');
      } else {
        toast.error('Erreur lors de la duplication.');
      }
    } catch {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsDuplicating(false);
    }
  };

  const openDuplicateSectionForm = (section) => {
    setDuplicatingSection(section);
    setNewSectionCopyName(section.name_section + ' (copie)');
  };

  const handleDuplicateSection = async (e) => {
    e.preventDefault();
    if (!newSectionCopyName.trim() || !duplicatingSection) return;
    setIsDuplicating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/duplicate_section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_id: duplicatingSection._id,
          new_section_name: newSectionCopyName.trim(),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setLocalSections((prev) => [...prev, data.section]);
        setLocalRooms((prev) => [...prev, ...data.rooms]);
        setLocalEquipments((prev) => [...prev, ...data.equipments]);
        setDuplicatingSection(null);
        setNewSectionCopyName('');
        toast.success('Section dupliquée. Pensez à affecter les usages de la nouvelle section.');
      } else {
        toast.error('Erreur lors de la duplication.');
      }
    } catch {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsDuplicating(false);
    }
  };

  const functionalUnits = buildingData?.functional_units || [];
  const { checkAndApply, cascadeModal } = useFunctionalUnitCascade();

  const handleSectionFunctionalUnitChange = (section, newValue) => {
    const previousValue = section.functional_unit || '';
    checkAndApply({
      scope: 'section',
      entityId: section._id,
      oldValue: previousValue,
      newValue,
      targetLabel: functionalUnits.find((u) => u.id === newValue)?.name || UNCATEGORIZED_LABEL,
      apply: async (cascadeSelection) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/update_section/${section._id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ functional_unit: newValue }),
          });
          if (!response.ok) throw new Error('Erreur API');
          if (cascadeSelection && (cascadeSelection.room_ids.length || cascadeSelection.usage_ids.length)) {
            await fetch(`${process.env.REACT_APP_API_URL}/reassign_functional_unit`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...cascadeSelection, new_unit_id: newValue || null }),
            });
          }
          setLocalSections((prev) => prev.map((s) => (s._id === section._id ? { ...s, functional_unit: newValue } : s)));
          if (cascadeSelection && cascadeSelection.room_ids.length) {
            setLocalRooms((prev) => prev.map((r) => (
              cascadeSelection.room_ids.includes(r._id) ? { ...r, functional_unit: newValue } : r
            )));
          }
          toast.success('Unité fonctionnelle mise à jour.');
        } catch (error) {
          toast.error("Erreur lors de la mise à jour de l'unité fonctionnelle.");
        }
      },
    });
  };

  const handleRoomFunctionalUnitChange = (room, newValue) => {
    const previousValue = room.functional_unit || '';
    checkAndApply({
      scope: 'room',
      entityId: room._id,
      oldValue: previousValue,
      newValue,
      targetLabel: functionalUnits.find((u) => u.id === newValue)?.name || UNCATEGORIZED_LABEL,
      apply: async (cascadeSelection) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/update_room/${room._id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ functional_unit: newValue }),
          });
          if (!response.ok) throw new Error('Erreur API');
          if (cascadeSelection && cascadeSelection.usage_ids.length) {
            await fetch(`${process.env.REACT_APP_API_URL}/reassign_functional_unit`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...cascadeSelection, new_unit_id: newValue || null }),
            });
          }
          setLocalRooms((prev) => prev.map((r) => (r._id === room._id ? { ...r, functional_unit: newValue } : r)));
          toast.success('Unité fonctionnelle mise à jour.');
        } catch (error) {
          toast.error("Erreur lors de la mise à jour de l'unité fonctionnelle.");
        }
      },
    });
  };

  return (
    <div className="reorg-view">
      <div className="reorg-instruction">
        Glissez les pièces entre sections : les modifications sont enregistrées immédiatement.
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="reorg-board">
          {localSections.map((section) => (
            <SectionColumn
              key={section._id}
              section={section}
              rooms={localRooms.filter((r) => r.id_section === section._id)}
              equipments={localEquipments}
              onDuplicate={openDuplicateForm}
              onDuplicateSection={openDuplicateSectionForm}
              functionalUnits={functionalUnits}
              onChangeSectionFunctionalUnit={handleSectionFunctionalUnitChange}
              onChangeRoomFunctionalUnit={handleRoomFunctionalUnitChange}
            />
          ))}

          <div className="reorg-add-section-col">
            {showNewSectionForm ? (
              <form className="reorg-new-section-form" onSubmit={handleCreateSection}>
                <input
                  autoFocus
                  type="text"
                  className="reorg-new-section-input"
                  placeholder="Nom de la section"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  required
                />
                <select
                  className="reorg-new-section-input"
                  value={newSectionFunctionalUnit}
                  onChange={(e) => setNewSectionFunctionalUnit(e.target.value)}
                >
                  <option value="">Unité fonctionnelle</option>
                  {functionalUnits.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
                <select
                  className="reorg-new-section-input"
                  value={newSectionFrequentation}
                  onChange={(e) => setNewSectionFrequentation(e.target.value)}
                >
                  <option value="low">Fréquentation faible</option>
                  <option value="average">Fréquentation moyenne</option>
                  <option value="high">Fréquentation forte</option>
                </select>
                <div className="reorg-new-section-actions">
                  <button
                    type="submit"
                    className="reorg-save-btn"
                    disabled={isCreatingSection || !newSectionName.trim()}
                  >
                    {isCreatingSection ? '…' : 'Créer'}
                  </button>
                  <button type="button" className="reorg-cancel-btn" onClick={resetNewSectionForm}>
                    ✕
                  </button>
                </div>
              </form>
            ) : (
              <button className="reorg-add-section-btn" onClick={() => setShowNewSectionForm(true)}>
                + Nouvelle section
              </button>
            )}
          </div>
        </div>

        <TrashDropZone visible={!!activeRoom} />

        <DragOverlay dropAnimation={null}>
          {activeRoom && <OverlayCard room={activeRoom} equipments={localEquipments} />}
        </DragOverlay>
      </DndContext>

      {duplicatingRoom && (
        <div className="section-popup">
          <div className="section-popup-content">
            <span className="close" onClick={() => setDuplicatingRoom(null)}>&times;</span>
            <h2>Dupliquer la pièce</h2>
            <p className="reorg-dup-subtitle">
              "{duplicatingRoom.name_room}" et ses équipements seront copiés.
              Les usages ne sont pas copiés : à réaffecter sur la nouvelle pièce.
            </p>
            <form onSubmit={handleDuplicateRoom}>
              <label htmlFor="newRoomName">Nom de la nouvelle pièce</label>
              <input
                id="newRoomName"
                autoFocus
                type="text"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                required
              />
              <button type="submit" className="add-equipment-button" disabled={isDuplicating}>
                {isDuplicating ? 'Duplication…' : 'Dupliquer'}
              </button>
            </form>
          </div>
        </div>
      )}

      {duplicatingSection && (
        <div className="section-popup">
          <div className="section-popup-content">
            <span className="close" onClick={() => setDuplicatingSection(null)}>&times;</span>
            <h2>Dupliquer la section</h2>
            <p className="reorg-dup-subtitle">
              "{duplicatingSection.name_section}", ses pièces et leurs équipements seront copiés.
              Les usages ne sont pas copiés : à réaffecter sur la nouvelle section.
            </p>
            <form onSubmit={handleDuplicateSection}>
              <label htmlFor="newSectionCopyName">Nom de la nouvelle section</label>
              <input
                id="newSectionCopyName"
                autoFocus
                type="text"
                value={newSectionCopyName}
                onChange={(e) => setNewSectionCopyName(e.target.value)}
                required
              />
              <button type="submit" className="add-equipment-button" disabled={isDuplicating}>
                {isDuplicating ? 'Duplication…' : 'Dupliquer'}
              </button>
            </form>
          </div>
        </div>
      )}

      {cascadeModal}
    </div>
  );
}
